import React, { useContext, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import { SYSTEM_COLLAPSE, USER_COLLAPSE } from 'common/actions/row-groups/types';
import { DeleteRowButton } from 'components/LedgerTable/components';
import FeaturedSpreadsheetContext, { FeaturedSpreadsheetContextType } from '../context/FeaturedSpreadsheetContext';
import { buildStripeRowParityMap } from '../utilities/buildStripeRowParityMap';
import { StripeRowConfig } from '../utilities/isStripeExemptRow';

// Define interfaces for the component props
interface Cell {
  parent?: string;
  [key: string]: any;
}

interface RowProps {
  cells: Cell[];
  children: React.ReactNode[];
  row: number;
  isLegend?: boolean;
}

const StyledRow = styled('tr')({
  '&:hover .delete-row': {
    opacity: 1,
  },
});

const StyledTableData = styled('td')({
  opacity: 0,
});

const Row: React.FC<RowProps> = ({ cells, children, row, isLegend }) => {
  const { rowGroups, displayLegend, isToolbarVisible, rows, displayDeleteRowBtn, deleteRowFn, stripeRowParityMap }
    = useContext<FeaturedSpreadsheetContextType>(FeaturedSpreadsheetContext);

  const isRowVisible = useMemo(() => {
    if (rowGroups && rows?.length) {
      const result = cells.find(cell => cell?.parent);

      if (result?.parent) {
        const getParentAliasOfRow = (rowAlias: string): string | undefined => {
          const configRow = rows.find((r: { alias?: string }) => r?.alias === rowAlias);
          return configRow?.parent as string | undefined;
        };

        // Walk ancestors (parent → grandparent → …) so nested rows hide when a higher group
        // is collapsed (e.g. share-value rows under an entity row when the share section is collapsed).
        let current: string | undefined = result.parent;
        const visited = new Set<string>();
        while (current) {
          if (visited.has(current)) {
            break;
          }
          visited.add(current);

          const rowGroup = rowGroups[current];
          if (rowGroup === SYSTEM_COLLAPSE || rowGroup === USER_COLLAPSE) {
            return false;
          }
          current = getParentAliasOfRow(current);
        }
      }
    }

    return !(!displayLegend && row === 0 && !isToolbarVisible);
  }, [rowGroups, cells, displayLegend, row, isToolbarVisible, rows]);

  const visibleClass = useMemo(() => (isRowVisible ? 'visible' : 'hidden'), [isRowVisible]);

  const logicalRowIndex = cells[0]?.x;
  const effectiveStripeRowParityMap = useMemo(() => {
    if (stripeRowParityMap) {
      return stripeRowParityMap;
    }

    const sheet = cells[0]?.sheet;
    if (!sheet?.rowConfig) {
      return new Map();
    }

    const stripeRowConfig: StripeRowConfig =
      'stripeRowConfig' in sheet
        ? (sheet as { stripeRowConfig: StripeRowConfig }).stripeRowConfig
        : { hasColTitle: sheet.hasColTitle };

    return buildStripeRowParityMap(sheet.rowConfig, rowGroups, stripeRowConfig);
  }, [stripeRowParityMap, cells, rowGroups]);

  const stripeParity = useMemo(() => {
    if (logicalRowIndex === undefined) {
      return undefined;
    }
    return effectiveStripeRowParityMap.get(logicalRowIndex);
  }, [logicalRowIndex, effectiveStripeRowParityMap]);

  // Check if this row is deletable (not virtual)
  const isRowDeletable = useMemo(() => {
    // Check if any cell in this row has isDeletableRow set to true
    // or if no cell has is_virtual set to true
    const firstCell = cells?.[0];
    if (firstCell) {
      // If isDeletableRow is explicitly set, use that
      if ('isDeletableRow' in firstCell) {
        return firstCell.isDeletableRow;
      }
      // Otherwise check if it's not virtual
      if ('is_virtual' in firstCell) {
        return !firstCell.is_virtual;
      }
    }
    // Default to true for backward compatibility
    return true;
  }, [cells]);

  return (
    <StyledRow
      className={clsx(
        visibleClass,
        isRowVisible && stripeParity && 'stripe-row',
        isRowVisible && stripeParity === 'even' && 'stripe-row--even'
      )}
    >
      {children}
      {displayDeleteRowBtn && deleteRowFn && !isLegend && row > 1 && row <= rows.length && isRowDeletable && (
        <StyledTableData className="delete-row">
          <DeleteRowButton rowIndex={row - 1} deleteRow={deleteRowFn} />
        </StyledTableData>
      )}
    </StyledRow>
  );
};

export default Row;
